import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments } from "@fortawesome/free-solid-svg-icons";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP } from "../global_variables";
import "./UserWelcomeScreen.css";

// Build a local Date from a schedule row's date (YYYY-MM-DD...) and optional
// time (HH:MM:SS). Slicing avoids UTC timestamps shifting the day by a timezone.
const toLocalDateTime = (dateDay, startTime) => {
  if (!dateDay) return null;
  const datePart = String(dateDay).slice(0, 10);
  const timePart = startTime ? String(startTime).slice(0, 8) : "00:00:00";
  const d = new Date(`${datePart}T${timePart}`);
  return isNaN(d.getTime()) ? null : d;
};

const formatAppointment = (date) =>
  date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const UserWelcomeScreen = ({ setSelectedSection }) => {
  const username = localStorage.getItem("username") || "Guest";
  const navigate = useNavigate();

  const [nextAppointment, setNextAppointment] = useState(null);
  const [apptLoading, setApptLoading] = useState(true);
  const [awaitingPayment, setAwaitingPayment] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(true);

  const navigateToScreen = (screen) => {
    setSelectedSection(screen);
    navigate("/UserHome");
  };

  // Next appointment — earliest upcoming, uncancelled schedule date.
  const fetchNextAppointment = useCallback(async () => {
    setApptLoading(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/calendars`, "get", []);
      const rows = res?.ok && Array.isArray(res.data) ? res.data : [];
      const now = new Date();
      const upcoming = rows
        .map((row) => ({ row, when: toLocalDateTime(row.date_day, row.start_time) }))
        .filter(({ when }) => when && when >= now)
        .sort((a, b) => a.when - b.when);
      setNextAppointment(upcoming.length ? { ...upcoming[0].row, when: upcoming[0].when } : null);
    } catch (error) {
      console.error("Error fetching next appointment:", error);
      setNextAppointment(null);
    } finally {
      setApptLoading(false);
    }
  }, []);

  // Invoices — number of bookings awaiting payment (confirmed but unpaid).
  const fetchAwaitingPayment = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/dashboard`, "get", {});
      const data = res?.ok ? res.data : null;
      const confirmedUnpaid = Number(data?.count?.count);
      if (Number.isFinite(confirmedUnpaid)) {
        setAwaitingPayment(confirmedUnpaid);
      } else if (Array.isArray(data?.bookings)) {
        setAwaitingPayment(data.bookings.filter((b) => b && b.paid === false).length);
      } else {
        setAwaitingPayment(null);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setAwaitingPayment(null);
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNextAppointment();
    fetchAwaitingPayment();
  }, [fetchNextAppointment, fetchAwaitingPayment]);

  const scheduleHighlight = apptLoading
    ? "Loading…"
    : nextAppointment
    ? formatAppointment(nextAppointment.when)
    : "No upcoming appointments";

  const hasUnpaid = !invoiceLoading && awaitingPayment > 0;

  const invoiceHighlight = invoiceLoading
    ? "Loading…"
    : awaitingPayment == null
    ? "Unavailable"
    : awaitingPayment === 0
    ? "All settled"
    : `${awaitingPayment} booking${awaitingPayment === 1 ? "" : "s"} to pay`;

  return (
    <div className="UWS-container">
      <div className="UWS-header">
        <div className="UWS-title-wrapper">
          <img
            className="UWS-home-icon"
            src={require("../images/home_icon.png")}
            alt="Home"
          />
          <h1 className="UWS-title">Hi {username}, Welcome Back</h1>
        </div>
        <p className="UWS-subtitle">
          Manage your care services, messages, and account details directly from
          your dashboard.
        </p>
      </div>

      <div className="UWS-grid">
        <div
          id="messages-button"
          className="UWS-card"
          onClick={() => navigateToScreen("Messages")}
        >
          <div className="UWS-icon-wrapper">
            <FontAwesomeIcon icon={faComments} className="UWS-fa-icon" />
          </div>
          <div className="UWS-card-info">
            <h3 className="UWS-card-title">Messages</h3>
            <p className="UWS-card-desc">Open your conversations</p>
            <p className="UWS-card-highlight">Click to view</p>
          </div>
        </div>

        <div
          id="schedule-button"
          className="UWS-card"
          onClick={() => navigateToScreen("Calendar")}
        >
          <div className="UWS-icon-wrapper">
            <img src={require("../images/calendar_icon.png")} alt="Calendar" />
          </div>
          <div className="UWS-card-info">
            <h3 className="UWS-card-title">Schedule</h3>
            <p className="UWS-card-desc">
              {nextAppointment ? nextAppointment.care_service_name || "Next appointment" : "Next appointment"}
            </p>
            <p className="UWS-card-highlight">{scheduleHighlight}</p>
          </div>
        </div>

        <div
          id="provider-button"
          className="UWS-card"
          onClick={() => navigateToScreen("Provider")}
        >
          <div className="UWS-icon-wrapper">
            <img src={require("../images/provider_icon.png")} alt="Providers" />
          </div>
          <div className="UWS-card-info">
            <h3 className="UWS-card-title">Care Providers</h3>
            <p className="UWS-card-desc">Browse available care staff</p>
            <p className="UWS-card-highlight">Click to view</p>
          </div>
        </div>

        <div
          id="invoices-button"
          className={`UWS-card ${hasUnpaid ? "UWS-card-attention" : ""}`}
          onClick={() => navigateToScreen("Booking")}
        >
          <div className="UWS-icon-wrapper">
            <img src={require("../images/invoice_icon.png")} alt="Invoices" />
            {hasUnpaid && (
              <span className="UWS-attention-badge" aria-hidden="true">
                {awaitingPayment}
              </span>
            )}
          </div>
          <div className="UWS-card-info">
            <h3 className="UWS-card-title">Invoices</h3>
            <p className="UWS-card-desc">Bookings awaiting payment</p>
            <p className={`UWS-card-highlight ${hasUnpaid ? "UWS-highlight-attention" : ""}`}>
              {invoiceHighlight}
            </p>
          </div>
        </div>

        <div
          id="account-button"
          className="UWS-card"
          onClick={() => navigateToScreen("Account")}
        >
          <div className="UWS-icon-wrapper">
            <img src={require("../images/house_icon.png")} alt="Account" />
          </div>
          <div className="UWS-card-info">
            <h3 className="UWS-card-title">Account</h3>
            <p className="UWS-card-desc">Manage your profile details</p>
            <p className="UWS-card-highlight">Click to view</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserWelcomeScreen;
